import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LayoutElement, FloorPlanBounds } from '../../types';
import { InstanceBatchResult } from '../../workers/scene3dCompute';

export type Layout3DStatus = 'idle' | 'loading' | 'ready' | 'error';

export type Layout3DState = {
  status: Layout3DStatus;
  boundsKey: string | null;
  elements: Record<string, LayoutElement>;
  elementOrder: string[];
  instanceBatches: InstanceBatchResult[] | null;
  objectCount: number;
  error: string | null;
  /** True when layout was auto-generated for 3D (vs user-placed 2D elements). */
  autoGenerated: boolean;
  /** Bounds from imported layout-3d JSON (used to fit camera when no DXF floor). */
  importBounds: FloorPlanBounds | null;
  /** Top-down plan view when imported layout has no elevation / z-index. */
  flatPlanView: boolean;
};

const initialState: Layout3DState = {
  status: 'idle',
  boundsKey: null,
  elements: {},
  elementOrder: [],
  instanceBatches: null,
  objectCount: 0,
  error: null,
  autoGenerated: false,
  importBounds: null,
  flatPlanView: false,
};

const layout3dSlice = createSlice({
  name: 'layout3d',
  initialState,
  reducers: {
    setLayout3dLoading: (state, action: PayloadAction<{ boundsKey: string }>) => {
      state.status = 'loading';
      state.boundsKey = action.payload.boundsKey;
      state.error = null;
      state.instanceBatches = null;
    },
    setLayout3dReady: (
      state,
      action: PayloadAction<{
        boundsKey: string;
        elements: Record<string, LayoutElement>;
        elementOrder: string[];
        instanceBatches: InstanceBatchResult[];
        autoGenerated: boolean;
        importBounds?: FloorPlanBounds | null;
        flatPlanView?: boolean;
      }>
    ) => {
      state.status = 'ready';
      state.boundsKey = action.payload.boundsKey;
      state.elements = action.payload.elements;
      state.elementOrder = action.payload.elementOrder;
      state.instanceBatches = action.payload.instanceBatches;
      state.objectCount = action.payload.elementOrder.length;
      state.autoGenerated = action.payload.autoGenerated;
      state.importBounds = action.payload.importBounds ?? null;
      state.flatPlanView = action.payload.flatPlanView ?? false;
      state.error = null;
    },
    setLayout3dImported: (
      state,
      action: PayloadAction<{
        boundsKey: string;
        importBounds: FloorPlanBounds;
        instanceBatches: InstanceBatchResult[] | null;
        flatPlanView?: boolean;
      }>
    ) => {
      state.status = action.payload.instanceBatches ? 'ready' : 'loading';
      state.boundsKey = action.payload.boundsKey;
      state.importBounds = action.payload.importBounds;
      state.instanceBatches = action.payload.instanceBatches;
      state.autoGenerated = false;
      state.flatPlanView = action.payload.flatPlanView ?? false;
      state.elements = {};
      state.elementOrder = [];
      state.objectCount = 0;
      state.error = null;
    },

    setLayout3dImportedBatches: (state, action: PayloadAction<InstanceBatchResult[]>) => {
      state.instanceBatches = action.payload;
      state.status = 'ready';
    },

    setLayout3dError: (state, action: PayloadAction<string>) => {
      state.status = 'error';
      state.error = action.payload;
    },
    clearLayout3d: () => initialState,
  },
});

export const {
  setLayout3dLoading,
  setLayout3dReady,
  setLayout3dImported,
  setLayout3dImportedBatches,
  setLayout3dError,
  clearLayout3d,
} = layout3dSlice.actions;

export default layout3dSlice.reducer;
